import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Contract, ContractType, ContractStatus } from '../../models/contract';
import { Client } from '../../models/client';
import { Property } from '../../models/property';
import { ImmobilierService } from '../../services/immobilier.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-add-edit-contrat-dialog',
    templateUrl: './add-edit-contrat-dialog.component.html',
    styleUrls: ['./add-edit-contrat-dialog.component.css']
})
export class AddEditContratDialogComponent implements OnInit {

    contract: Contract;
    isNew: boolean = true;

    // Data sources
    clients: Client[] = [];
    properties: Property[] = [];

    // Dropdown options
    contractTypes: ContractType[] = ['location', 'vente', 'mandat', 'gerance'];
    contractStatuses: ContractStatus[] = ['brouillon', 'en_cours', 'signe', 'termine', 'resilie', 'en_negociation', 'expire', 'archive'];
    paymentMethods: string[] = ['virement', 'cheque', 'especes', 'mobile_money'];

    // Custom Stepper
    currentStep = 0;
    steps: string[] = [
        'Informations Générales',
        'Parties Concernées',
        'Bien Immobilier',
        'Conditions Financières',
        'Durée & Clauses',
        'Documents'
    ];

    constructor(
        public dialogRef: MatDialogRef<AddEditContratDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private immobilierService: ImmobilierService,
        private snackBar: MatSnackBar
    ) {
        if (data && data.contract) {
            this.contract = { ...data.contract, ownerName: 'MBOUFTA', ownerId: 1 };
            this.isNew = false;
        } else {
            this.isNew = true;
            this.contract = {
                id: 0,
                contractNumber: this.generateContractNumber(),
                type: 'location',
                propertyId: 0,
                ownerId: 1,
                ownerName: 'MBOUFTA',
                tenantBuyerId: 0,
                startDate: new Date(),
                status: 'brouillon',
                createdAt: new Date(),
                updatedAt: new Date()
            };
        }
    }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.immobilierService.getClients().subscribe(clients => this.clients = clients);
        this.immobilierService.getProperties().subscribe(properties => this.properties = properties);
    }

    generateContractNumber(): string {
        const date = new Date();
        const year = date.getFullYear();
        let random = Math.floor(Math.random() * 1000).toString();
        while (random.length < 3) {
            random = '0' + random;
        }
        return `CTR-${year}-${random}`;
    }

    // Stepper Navigation
    nextStep(): void {
        if (this.currentStep < this.steps.length - 1) {
            this.currentStep++;
        }
    }

    prevStep(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
        }
    }

    setStep(index: number): void {
        this.currentStep = index;
    }

    // Actions
    save(): void {
        const file = this.selectedFile || undefined;

        if (this.isNew) {
            this.immobilierService.createContract(this.contract, file).subscribe({
                next: (res) => {
                    this.snackBar.open('Contrat créé avec succès', 'Fermer', { duration: 3000 });
                    this.dialogRef.close(res);
                },
                error: (err) => {
                    console.error(err);
                    this.snackBar.open('Erreur lors de la création', 'Fermer', { duration: 3000 });
                }
            });
        } else {
            this.immobilierService.updateContract(this.contract.id, this.contract, file).subscribe({
                next: (res) => {
                    this.snackBar.open('Contrat mis à jour avec succès', 'Fermer', { duration: 3000 });
                    this.dialogRef.close(res);
                },
                error: (err) => {
                    console.error(err);
                    this.snackBar.open('Erreur lors de la mise à jour', 'Fermer', { duration: 3000 });
                }
            });
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    // Helpers
    onPropertyChange(propertyId: number): void {
        const property = this.properties.find(p => p.id == propertyId);
        if (property) {
            this.contract.propertyReference = property.reference;
            // Auto-fill financial info if available
            if (this.contract.type === 'location') {
                this.contract.monthlyRent = property.price;
                this.contract.charges = property.charges;
                this.contract.deposit = property.deposit;
                this.contract.fees = property.fees;
            } else if (this.contract.type === 'vente') {
                this.contract.salePrice = property.price;
                this.contract.fees = property.fees;
            }
        }
    }

    onClientChange(clientId: number, role: 'owner' | 'tenant'): void {
        const client = this.clients.find(c => c.id == clientId);
        if (client) {
            if (role === 'owner') {
                this.contract.ownerName = `${client.firstName} ${client.lastName}`;
            } else {
                this.contract.tenantBuyerName = `${client.firstName} ${client.lastName}`;
            }
        }
    }

    // Document Management
    selectedFile: File | null = null;

    onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.selectedFile = file;
            // Simulation : on stocke le nom du fichier dans l'URL pour l'instant
            this.contract.contractDocumentUrl = file.name;
            this.snackBar.open(`Fichier ${file.name} sélectionné`, 'Fermer', { duration: 2000 });
        }
    }

    removeFile(): void {
        this.selectedFile = null;
        this.contract.contractDocumentUrl = undefined;
    }
}
